/* eslint-disable @next/next/no-img-element */
import React, { FC, useContext, useEffect, useState } from "react";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import Link from "next/link";
import RockstarFooter from "components/RockstarFooter";
import RockstarHeader from "components/RockstarHeader";
import { LogContext } from "context/log";
import {
  AllGamingArticlesQuery,
  CategoryBySlugQuery,
  getSdk,
} from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import DatoNativeHeadTagsRenderer from "components/DatoNativeHeadTagsRenderer";
import RockstarRelatedProducts from "components/RockstarRelatedProducts";
import TopCtaBanner from "components/TopCtaBanner";
import { PAGE_TYPE_ENUM } from "@pepdirect/shared/types";
import { generatePageId } from "@pepdirect/helpers/analyticsLogger";
import { Button } from "@pepdirect/ui/button";

interface GamingPageProps {
  game: AllGamingArticlesQuery["allArticles"];
}

const DEFAULT_CTA_TEXT = "SEE MORE";

export const getServerSideProps: GetServerSideProps<GamingPageProps> = async (
  context
) => {
  try {
    const { allArticles } = await getSdk(
      createDatoClient(context.preview)
    ).AllGamingArticles();

    return {
      props: {
        game: allArticles || [],
      },
    };
  } catch (e) {
    return {
      props: {
        game: [],
      },
    };
  }
};

const Gaming: FC<GamingPageProps> = ({ game }) => {
  const { useLogPageView } = useContext(LogContext);
  const router = useRouter();
  const [categoryContent, setCategoryContent] = useState<
    CategoryBySlugQuery["allCategories"][number] | null
  >(null);

  useLogPageView({
    pageType: PAGE_TYPE_ENUM.other,
    pageCategory: undefined,
    pageTitle: "gaming",
    id: generatePageId("gaming"),
  });

  useEffect(() => {
    getSdk(createDatoClient(router?.isPreview))
      .CategoryBySlug({ slug: "gaming" })
      .then(({ allCategories }) => {
        setCategoryContent(allCategories?.[0]);
      });
  }, [router]);

  return (
    <>
      {categoryContent?._seoMetaTags && (
        <DatoNativeHeadTagsRenderer seoTags={categoryContent?._seoMetaTags} />
      )}

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              <div className="culture-banner">
                <div
                  className="culture-banner-inner"
                  style={{
                    backgroundImage: `url(${categoryContent?.heroImage?.url})`,
                  }}
                >
                  <h1>{categoryContent?.title}</h1>
                </div>
                <div className="container">
                  <div className="culture-columns">
                    {game?.map((gameItem) => (
                      <div key={gameItem?.id} className="culture-small-item">
                        <div className="culture-title">
                          <h2>{gameItem?.title}</h2>
                        </div>
                        <Link href={`/${gameItem?.slug}`}>
                          <div className="culture-img">
                            <div
                              className="culture-img-inner"
                              style={{
                                backgroundImage: `url(${gameItem?.heroImage?.url})`,
                              }}
                            ></div>
                          </div>
                        </Link>

                        <div className="culture-details">
                          <div>{gameItem?.excerpt || ""}</div>

                          {gameItem?.slug && (
                            <div className="culture-btn-wrapper">
                              <Button
                                internalLink={`/${gameItem?.slug}`}
                                title={gameItem?.title || ""}
                              >
                                <div className="culture-btn">
                                  {DEFAULT_CTA_TEXT}
                                </div>
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <RockstarRelatedProducts />
              </div>
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
    </>
  );
};

export default Gaming;
